import { memo, useMemo, useState } from 'react';
import { ExternalLink, Info, ShieldAlert, TrendingDown, TrendingUp } from 'lucide-react';
import { confidenceTone, formatLabel } from './semantics';
import { BUSINESS_AREA_COLORS, businessAreaLabel, insightSentiment } from './executive';
import Tooltip from '../../ui/Tooltip';
import type { ArticleInsightLink, ArticleRiskLink, KeyInsight, NewsArticle } from './types';

interface BusinessInsightsProps {
  insights: KeyInsight[];
  articleInsightLinks: ArticleInsightLink[];
  articleRiskLinks?: ArticleRiskLink[];
  // Full article list so we can resolve article_insight_links to actual
  // articles and render publisher pills on each insight card. Optional
  // for callers that don't have the article corpus available — when
  // omitted, cards fall back to showing the bare count.
  articles?: NewsArticle[];
}

function buildArticleCounts(
  insights: KeyInsight[],
  links: ArticleInsightLink[]
): Record<string, number> {
  const m = new Map<string, Set<string>>();
  for (const l of links) {
    if (!m.has(l.insight_id)) m.set(l.insight_id, new Set());
    m.get(l.insight_id)!.add(l.article_id);
  }
  const out: Record<string, number> = {};
  for (const i of insights) out[i.insight_id] = m.get(i.insight_id)?.size ?? 0;
  return out;
}

/**
 * Resolve `article_insight_links` to actual NewsArticle records, grouped
 * by insight_id. Used to render publisher pills on each insight card.
 *
 * Returns Map<insight_id, NewsArticle[]> so each card knows which articles
 * support its strategic theme. Deduped on article_id to avoid the same
 * article appearing twice if the LLM emitted duplicate links. Returns an
 * empty map if `articles` is undefined — callers fall back to count-only
 * display.
 */
function buildLinkedArticles(
  insights: KeyInsight[],
  links: ArticleInsightLink[],
  articles: NewsArticle[] | undefined,
): Record<string, NewsArticle[]> {
  const out: Record<string, NewsArticle[]> = {};
  if (!articles || articles.length === 0) return out;

  // Lookup table for fast article_id → NewsArticle resolution.
  const byId = new Map<string, NewsArticle>();
  for (const a of articles) byId.set(a.article_id, a);

  // For each insight, gather its linked articles in link-emission order
  // (which mirrors the order the LLM listed them — usually most-relevant
  // first). De-dupe in case a (insight_id, article_id) pair was emitted
  // twice.
  for (const ins of insights) {
    const seen = new Set<string>();
    const collected: NewsArticle[] = [];
    for (const link of links) {
      if (link.insight_id !== ins.insight_id) continue;
      if (seen.has(link.article_id)) continue;
      const article = byId.get(link.article_id);
      if (!article) continue;
      seen.add(link.article_id);
      collected.push(article);
    }
    out[ins.insight_id] = collected;
  }
  return out;
}

function buildRiskCounts(
  insights: KeyInsight[],
  articleInsightLinks: ArticleInsightLink[],
  articleRiskLinks: ArticleRiskLink[]
): Record<string, number> {
  // article → risk ids
  const artRisks = new Map<string, Set<string>>();
  articleRiskLinks.forEach((l) => {
    if (!artRisks.has(l.article_id)) artRisks.set(l.article_id, new Set());
    artRisks.get(l.article_id)!.add(l.risk_id);
  });
  // insight → article ids
  const insArticles = new Map<string, Set<string>>();
  articleInsightLinks.forEach((l) => {
    if (!insArticles.has(l.insight_id)) insArticles.set(l.insight_id, new Set());
    insArticles.get(l.insight_id)!.add(l.article_id);
  });
  const out: Record<string, number> = {};
  for (const ins of insights) {
    const arts = insArticles.get(ins.insight_id) ?? new Set<string>();
    const risks = new Set<string>();
    arts.forEach((aid) => artRisks.get(aid)?.forEach((rid) => risks.add(rid)));
    out[ins.insight_id] = risks.size;
  }
  return out;
}

// ─── supporting-articles pill strip ─────────────────────────────────────────

// Maximum number of pills shown before the overflow chip kicks in.
// Tuned so 6 publisher names fit on a single row at the typical card width
// without wrapping. Beyond 6, a "+N" chip indicates remaining articles and
// clicking it expands the full list inline.
const PILL_VISIBLE_LIMIT = 6;

function SupportingArticles({ articles }: { articles: NewsArticle[] }) {
  const [expanded, setExpanded] = useState(false);

  // Label resolution per pill. When the publisher's `domain` collapses to
  // a Google News redirect (decoder couldn't resolve to the real publisher
  // URL), fall back to the RSS `source` name so the pill stays readable.
  const labelFor = (article: NewsArticle): string => {
    const isAggregatorDomain =
      !article.domain ||
      /(^|\.)news\.google\.com$/i.test(article.domain) ||
      /(^|\.)google\.com$/i.test(article.domain);
    return isAggregatorDomain
      ? article.source || article.display_title || article.domain || 'Source'
      : article.domain ?? 'Source';
  };

  // Show first 6 pills; the rest are revealed when the "+N" chip is
  // clicked. Each pill is its own clickable chip linking to the actual
  // article — no grouping, no count badges, so the user can reach any
  // specific story in one click.
  const visible = expanded ? articles : articles.slice(0, PILL_VISIBLE_LIMIT);
  const overflow = articles.length - PILL_VISIBLE_LIMIT;

  return (
    <div className="mt-4">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-400">
        Supporting articles
      </p>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((article) => {
          const label = labelFor(article);
          const tooltip = article.display_title || article.source || label;
          return (
            <Tooltip key={article.article_id} content={tooltip} maxWidth={300}>
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-[220px] items-center gap-1 truncate rounded-full border border-primary-100 bg-primary-50 px-2.5 py-1 text-[11px] font-medium text-primary-700 transition-colors hover:border-primary-300 hover:bg-primary-100 hover:text-primary-800"
              >
                <span className="truncate">{label}</span>
                <ExternalLink className="h-2.5 w-2.5 shrink-0 text-primary-400" />
              </a>
            </Tooltip>
          );
        })}
        {!expanded && overflow > 0 && (
          <Tooltip content={`Show ${overflow} more`}>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center rounded-full border border-primary-200 bg-white px-2.5 py-1 text-[11px] font-bold text-primary-700 transition-colors hover:border-primary-400 hover:bg-primary-50"
            >
              +{overflow}
            </button>
          </Tooltip>
        )}
        {expanded && articles.length > PILL_VISIBLE_LIMIT && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-500 transition-colors hover:border-neutral-300 hover:bg-neutral-50"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

// ─── single insight card ──────────────────────────────────────────────────────

function InsightCard({
  insight,
  rank,
  articleCount,
  riskCount,
  linkedArticles,
}: {
  insight: KeyInsight;
  articleCount: number;
  riskCount: number;
  // Kept for API stability with the parent; used only to colour the left
  // accent stripe so the first 2 cards visually anchor the section.
  rank: number;
  // Articles that the LLM linked to this insight, in emission order
  // (most-relevant first). Each becomes a clickable publisher pill at
  // the bottom of the card. Empty array = no linked articles (rare; the
  // synthesis prompt requires every insight to have ≥1 link).
  linkedArticles: NewsArticle[];
}) {
  const isContrarian = !!insight.contrarian;

  // Prose source for the card body — prefer market_interpretation (LLM's
  // investor-friendly reading), fall back through fact, then implication.
  // implication is the conditional arrow-laden string ("Revenue ↑ (if X)")
  // and is only used when nothing better is available.
  const body =
    insight.market_interpretation ||
    insight.fact ||
    insight.implication ||
    '';

  const area = businessAreaLabel(`${insight.insight_label} ${body}`);
  const areaStyle = BUSINESS_AREA_COLORS[area] ?? BUSINESS_AREA_COLORS['Strategic'];
  const signal = insight.sentiment ?? insightSentiment(insight.insight_label, insight.implication);
  const confTone = confidenceTone(insight.confidence);

  const accentClass = isContrarian
    ? 'bg-amber-400'
    : rank === 0
    ? 'bg-secondary-500'
    : rank === 1
    ? 'bg-primary-400'
    : 'bg-neutral-200';

  const SignalIcon =
    signal === 'bullish' ? TrendingUp : signal === 'bearish' ? TrendingDown : null;
  const signalTone =
    signal === 'bullish'
      ? 'text-emerald-700'
      : signal === 'bearish'
      ? 'text-rose-700'
      : 'text-neutral-500';

  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-xl border bg-white ${
        isContrarian ? 'border-amber-200' : 'border-neutral-200'
      }`}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${accentClass}`} aria-hidden />

      <div className="flex flex-1 flex-col p-5 pl-6">
        {/* Metadata row — single line, minimal pills.
            Left: area chip + sentiment text (icon + word, no separate pill).
            Right: subtle confidence label, only when present. */}
        <div className="mb-2.5 flex flex-wrap items-center gap-2 text-[11px]">
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${areaStyle}`}
          >
            {area}
          </span>
          {!isContrarian && (
            <span className={`inline-flex items-center gap-0.5 font-semibold ${signalTone}`}>
              {SignalIcon && <SignalIcon className="h-3 w-3" />}
              {signal === 'bullish' ? 'Bullish' : signal === 'bearish' ? 'Bearish' : 'Neutral'}
            </span>
          )}
          {isContrarian && (
            <span className="inline-flex items-center gap-0.5 font-semibold text-amber-700">
              Contrarian view
            </span>
          )}
          {insight.confidence && (
            <span className={`ml-auto text-[10px] font-medium uppercase tracking-wide ${confTone.text}`}>
              {formatLabel(insight.confidence)} confidence
            </span>
          )}
        </div>

        {/* Title */}
        <h4 className="text-[15px] font-bold leading-snug text-neutral-900">
          {insight.insight_label}
        </h4>

        {/* Primary prose — the only paragraph the user reads by default. */}
        {body && (
          <p className="mt-2 text-[13px] leading-relaxed text-neutral-700">
            {body}
          </p>
        )}

        {/* Secondary prose — strategic_meaning sits below as a quieter line.
            Skipped when the LLM didn't produce a distinct second sentence. */}
        {insight.strategic_meaning && insight.strategic_meaning !== body && (
          <p className="mt-2 text-[12px] leading-relaxed text-neutral-500">
            {insight.strategic_meaning}
          </p>
        )}

        {/* Unknown — inline single-line note with an info icon, no colored
            box. Communicates "here's what's still uncertain" without
            visually competing with the main paragraph. */}
        {insight.unknown && (
          <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-neutral-500">
            <Info className="mt-0.5 h-3 w-3 shrink-0 text-neutral-400" />
            <span>
              <span className="font-semibold text-neutral-600">Unclear: </span>
              {insight.unknown}
            </span>
          </p>
        )}

        {/* Supporting article pills — one clickable chip per publisher
            that reported on this insight's underlying news. Sits above the
            risk/FY metadata footer so the source attribution is visible
            without scrolling. Resolved from article_insight_links by the
            parent.
            Style intent (matches the reference design): light blue fill
            with blue text by default, deeper-blue hover. Prefer publisher
            domain over source name on the pill label so the user can
            instantly recognize the outlet (e.g., "reuters.com" beats
            "Reuters" because the URL → identity mapping is more familiar
            on news content). */}
        {linkedArticles.length > 0 && (
          <SupportingArticles articles={linkedArticles} />
        )}

        {/* Footer — drops the redundant "N supporting articles" text since
            the pills above already show the actual sources. Keeps risk
            count + related_fy because those aren't otherwise visible. */}
        {(riskCount > 0 || insight.related_fy) && (
          <div className="mt-3 flex items-center gap-3 pt-1 text-[11px] text-neutral-400">
            {riskCount > 0 && (
              <span className="inline-flex items-center gap-0.5 text-rose-500">
                <ShieldAlert className="h-3 w-3" />
                {riskCount} {riskCount === 1 ? 'risk' : 'risks'}
              </span>
            )}
            {insight.related_fy && (
              <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500">
                {insight.related_fy}
              </span>
            )}
          </div>
        )}

        {/* Defensive: when no articles got linked AND no other metadata
            exists, still surface the count so an empty card doesn't lie
            about its evidence base. Only fires when linkedArticles is
            empty but articleCount > 0 (which means linkedArticles
            resolution failed for some reason). */}
        {linkedArticles.length === 0 && articleCount > 0 && (
          <p className="mt-3 text-[11px] text-neutral-400">
            {articleCount} supporting {articleCount === 1 ? 'article' : 'articles'}
          </p>
        )}
      </div>
    </div>
  );
}

const MemoInsightCard = memo(InsightCard);

// ─── section ──────────────────────────────────────────────────────────────────

function BusinessInsightsBase({
  insights,
  articleInsightLinks,
  articleRiskLinks = [],
  articles,
}: BusinessInsightsProps) {
  const articleCounts = useMemo(
    () => buildArticleCounts(insights, articleInsightLinks),
    [insights, articleInsightLinks]
  );
  const riskCounts = useMemo(
    () => buildRiskCounts(insights, articleInsightLinks, articleRiskLinks),
    [insights, articleInsightLinks, articleRiskLinks]
  );
  // Resolve each insight to its actual NewsArticle list (for the pill
  // strip). Recomputed when articles or links change — cheap O(N×M)
  // dictionary build, runs once per render.
  const linkedArticlesByInsight = useMemo(
    () => buildLinkedArticles(insights, articleInsightLinks, articles),
    [insights, articleInsightLinks, articles]
  );

  // Render every non-contrarian insight. The backend prompt now scales the
  // count to the corpus size (7-10 for ≥30 analyzed articles), so an arbitrary
  // .slice(0, 6) here desyncs the header count from the visible cards.
  const main = useMemo(() => insights.filter((i) => !i.contrarian), [insights]);
  const contrarian = useMemo(() => insights.filter((i) => i.contrarian), [insights]);

  if (insights.length === 0) return null;

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-800">
            Key Business Insights
          </h2>
          <p className="mt-1 text-xs text-neutral-500">
            What the news signals for strategic decisions
          </p>
        </div>
        <span className="shrink-0 text-[11px] font-semibold text-neutral-500">
          {insights.length} signal{insights.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className={`grid gap-4 ${main.length === 1 ? 'grid-cols-1' : 'sm:grid-cols-2'}`}>
        {main.map((insight, i) => (
          <MemoInsightCard
            key={insight.insight_id}
            insight={insight}
            rank={i}
            articleCount={articleCounts[insight.insight_id] ?? 0}
            riskCount={riskCounts[insight.insight_id] ?? 0}
            linkedArticles={linkedArticlesByInsight[insight.insight_id] ?? []}
          />
        ))}
      </div>

      {contrarian.length > 0 && (
        <div className="mt-5">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-amber-600">
            Contrarian Perspectives
          </p>
          <div className={`grid gap-4 ${contrarian.length === 1 ? 'grid-cols-1' : 'sm:grid-cols-2'}`}>
            {contrarian.map((insight, i) => (
              <MemoInsightCard
                key={insight.insight_id}
                insight={insight}
                rank={i}
                articleCount={articleCounts[insight.insight_id] ?? 0}
                riskCount={riskCounts[insight.insight_id] ?? 0}
                linkedArticles={linkedArticlesByInsight[insight.insight_id] ?? []}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export const BusinessInsights = memo(BusinessInsightsBase);
